import { useTheme } from '@emotion/react';
import { TextField } from '@mui/material';
import AutoNumeric from 'autonumeric';
import arySystemSettings from 'framework/base/constants/arySystemSettings';
import useShowCurrency from 'framework/base/hooks/useShowCurrency';
import useSystemSettingsContext from 'framework/base/hooks/useSystemSettingsContext';
import Num2persian from 'num2persian';
import { toWords as Num2English } from 'number-to-words';
import React, { useEffect, useRef, useState } from 'react';

export default function AryMobileCommaSeparatorTextField(props) {
  const {
    decimalPlaces,
    inputAdornment,
    value,
    removeAdditionalPadding,
    preDefined,
    onChange,
    onFocus,
    onBlur,
    onKeyPress,
    onKeyUp,
    onKeyDo,
    InputProps,
    currencyCode,
    showHelperText,
    helperText,
    ...others
  } = props;

  const [shouldShrink, setShouldShrink] = useState(false);

  const inputRef = useRef(null);
  const autoNumberRef = useRef(null);

  const { systemSettings } = useSystemSettingsContext();
  const theme = useTheme();

  useEffect(() => {
    if (others.focus) {
      inputRef.current?.focus();
    }
  }, [others.focus]);

  const safeGetNumber = () => {
    try {
      if (!autoNumberRef.current) return '';

      // Important:
      // rawValue can temporarily be "-" while the user is typing
      const { rawValue = '' } = autoNumberRef.current;

      if (rawValue === '') return '';
      if (rawValue === '-') return '-';

      const raw = autoNumberRef.current.getNumericString();

      if (raw === '') return '';
      if (raw === '-') return '-';

      const number = Number(raw);

      return Number.isNaN(number) ? '' : number;
    } catch (e) {
      return '';
    }
  };

  const callEventHandler = (event, eventName) => {
    if (
      eventName === 'onBlur' &&
      (!autoNumberRef.current || autoNumberRef.current.rawValue === '')
    ) {
      setShouldShrink(false);
    } else {
      setShouldShrink(true);
    }

    const { [eventName]: propsEventName } = props;

    if (!propsEventName) return;

    requestAnimationFrame(() => {
      event.numberValue = safeGetNumber();

      propsEventName(event);
    });
  };

  const onKeyDownHandler = (event, eventName) => {
    const { [eventName]: propsEventName } = props;

    if (!propsEventName) return;

    requestAnimationFrame(() => {
      event.numberValue = safeGetNumber();

      propsEventName(event);
    });
  };

  useEffect(() => {
    if (autoNumberRef.current) {
      try {
        autoNumberRef.current.remove();
      } catch (e) {
        /* ignore removal errors */
      }
    }

    autoNumberRef.current = new AutoNumeric(inputRef.current, value ?? '', {
      allowDecimalPadding: false,

      // Important: allow negative values
      minimumValue: '-1000000000000000000',
      maximumValue: '1000000000000000000',

      decimalPlaces: decimalPlaces !== undefined ? decimalPlaces : 2,

      ...preDefined,
      ...others,

      watchExternalChanges: false,
      modifyValueOnWheel: false
    });

    return () => {
      if (autoNumberRef.current) {
        try {
          autoNumberRef.current.remove();
        } catch (e) {
          /* ignore removal errors */
        }

        autoNumberRef.current = null;
      }
    };
  }, [decimalPlaces]);

  useEffect(() => {
    if (!autoNumberRef.current || !inputRef.current) return;

    const incomingVal =
      value === '' || value === null || value === undefined ? '' : value;

    /*
     * Important:
     *
     * When typing a negative number, the input sequence is:
     *
     * -
     * -1
     * -12
     *
     * "-" is not a complete numeric value for AutoNumeric.set().
     * Calling set('-') can cause AutoNumeric to clear it.
     *
     * So while the user is in this temporary state,
     * leave AutoNumeric/Input alone.
     */
    if (incomingVal === '-') {
      setShouldShrink(true);
      return;
    }

    try {
      autoNumberRef.current.set(incomingVal);
    } catch (e) {
      return;
    }

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set;

    const formatted = autoNumberRef.current.getFormatted();

    setTimeout(() => {
      if (!inputRef.current) return;

      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(inputRef.current, formatted);
      } else {
        inputRef.current.value = formatted;
      }

      inputRef.current.dispatchEvent(
        new Event('input', {
          bubbles: true
        })
      );
    }, 0);

    setShouldShrink(incomingVal !== '');
  }, [value]);

  const showCurrency = useShowCurrency();

  const generateTextValue = (valueAmount, currencyCodeAmount) => {
    if (
      valueAmount === '' ||
      valueAmount === null ||
      valueAmount === undefined ||
      valueAmount === '-'
    ) {
      return '';
    }

    const finalValueAmount = +String(valueAmount).replaceAll(',', '');

    const precision = decimalPlaces !== undefined ? 10 ** decimalPlaces : 200;

    const fixedPrecision = precision || 200;

    const finalAmount = Number.isFinite(finalValueAmount)
      ? Math.round(finalValueAmount * fixedPrecision) / fixedPrecision
      : NaN;

    let Num2Text;

    const num2EnglishWithDecimal = (num) => {
      if (Number.isNaN(num)) return '';

      const strNum = num.toString();

      if (!strNum.includes('.')) {
        return Num2English(num);
      }

      const [integerPart, decimalPart] = strNum.split('.');

      let text = Num2English(parseInt(integerPart, 10));

      if (parseInt(decimalPart, 10) !== 0) {
        const decimalNumber = parseInt(decimalPart, 10);
        const decimalText = Num2English(decimalNumber);

        text += ` point ${decimalText}`;
      }

      return text;
    };

    if (!Number.isNaN(finalAmount)) {
      Num2Text =
        systemSettings?.[arySystemSettings.systemLanguage] !== 'en'
          ? Num2persian(finalAmount)
          : num2EnglishWithDecimal(finalAmount);
    }

    return finalAmount !== 0 && finalAmount <= 0.0000009
      ? ''
      : currencyCodeAmount
      ? `${Num2Text} ${showCurrency({
          code: currencyCodeAmount
        })}`
      : '';
  };

  const helperNumberForText = (() => {
    if (!autoNumberRef.current) {
      return value ?? '';
    }

    const { rawValue = '' } = autoNumberRef.current;

    if (rawValue === '' || rawValue === '-') {
      return '';
    }

    const raw = autoNumberRef.current.getNumericString();

    if (raw === '' || raw === '-') {
      return '';
    }

    const num = Number(raw);

    return Number.isNaN(num) ? '' : num;
  })();

  return (
    <TextField
      inputRef={(ref) => {
        inputRef.current = ref;
      }}
      helperText={
        showHelperText ||
        helperText ||
        generateTextValue(helperNumberForText, currencyCode)
      }
      onChange={(event) => callEventHandler(event, 'onChange')}
      onFocus={(event) => callEventHandler(event, 'onFocus')}
      onBlur={(event) => callEventHandler(event, 'onBlur')}
      onKeyPress={(event) => callEventHandler(event, 'onKeyPress')}
      onKeyUp={(event) => callEventHandler(event, 'onKeyUp')}
      onKeyDown={(event) => onKeyDownHandler(event, 'onKeyDown')}
      InputProps={{
        ...InputProps,
        ...inputAdornment
      }}
      className={removeAdditionalPadding ? 'AryCurrencyTextField' : undefined}
      InputLabelProps={{
        shrink: shouldShrink
      }}
      autoComplete="off"
      inputProps={{
        style: {
          paddingLeft: theme.direction === 'ltr' ? '0.875rem' : '0',
          paddingRight: theme.direction === 'rtl' ? '0.875rem' : '0'
        }
      }}
      {...others}
    />
  );
}
